import { Extension, JSONContent, mergeAttributes, Node } from "@tiptap/core";
import { Fragment, Node as PMNode, Schema, Slice } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { badgeVariants } from "../ui/badge";

/**
 * EditorVariable is a placeholder in a text, which is replaced by
 * whoever renders the text, e.g. `$Preise` by a table in a PDF.
 * `name` includes the leading `$`.
 */
export type EditorVariable = {
  name: string;
  label: string;
  description?: string;
};

/**
 * findEditorVariables returns the positions of the registered variable
 * `names` in `text`. A variable is a `$` followed by letters, digits and
 * underscores, and only whole names match: `$PreiseNetto` is not
 * `$Preise`.
 */
export function findEditorVariables(
  text: string,
  names: string[],
): { from: number; to: number; name: string }[] {
  const result: { from: number; to: number; name: string }[] = [];
  for (const match of text.matchAll(/\$[\p{L}\p{N}_]+/gu)) {
    if (names.includes(match[0])) {
      result.push({
        from: match.index,
        to: match.index + match[0].length,
        name: match[0],
      });
    }
  }
  return result;
}

/**
 * EditorVariables is a Tiptap extension highlighting the registered
 * `variables` typed into the text, which stay text until the editor is
 * loaded again and they become an {@link EditorVariableNode}. The
 * highlight is a decoration only, so the generated HTML keeps the
 * variables as plain text.
 *
 * Variables are recognised within a single text node: a variable split
 * by a mark, e.g. partly bold, is not highlighted.
 */
export const EditorVariables = Extension.create<{
  variables: EditorVariable[];
}>({
  name: "editorVariables",

  addOptions() {
    return { variables: [] };
  },

  addProseMirrorPlugins() {
    const names = this.options.variables.map((v) => v.name);

    return [
      new Plugin({
        key: new PluginKey("editorVariables"),
        props: {
          decorations(state) {
            const decorations: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (!node.isText || !node.text) {
                return;
              }
              for (const { from, to } of findEditorVariables(
                node.text,
                names,
              )) {
                decorations.push(
                  Decoration.inline(pos + from, pos + to, {
                    class: badgeVariants({ variant: "default" }),
                  }),
                );
              }
            });
            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});

/** editorVariableNodeName is the type of an {@link EditorVariableNode}. */
export const editorVariableNodeName = "editorVariable";

/**
 * EditorVariableNode is a Tiptap node showing a registered variable as a
 * single chip with its label, e.g. "Preise" for `$Preise`, so users read
 * it in German. It is atomic: the cursor and backspace treat it as a
 * whole. It exists in the editor only; the generated HTML holds the
 * variable's name as plain text, see {@link variableNodesToText}, so the
 * renderers of the text are unaware of it. Variables in loaded and
 * pasted text become nodes, see {@link textToVariableNodes}.
 */
export const EditorVariableNode = Node.create<{
  variables: EditorVariable[];
}>({
  name: editorVariableNodeName,
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return { variables: [] };
  },

  addAttributes() {
    return {
      name: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-editor-variable"),
        renderHTML: (attributes) => ({
          "data-editor-variable": attributes.name,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-editor-variable]" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const name = node.attrs.name as string;
    const label =
      this.options.variables.find((v) => v.name === name)?.label || name;
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        class: badgeVariants({ variant: "default" }),
        title: name,
      }),
      label,
    ];
  },

  renderText({ node }) {
    return node.attrs.name as string;
  },

  addProseMirrorPlugins() {
    const names = this.options.variables.map((v) => v.name);

    return [
      new Plugin({
        key: new PluginKey("editorVariableNodePaste"),
        props: {
          transformPasted(slice, view) {
            return new Slice(
              variableNodesInFragment(slice.content, view.state.schema, names),
              slice.openStart,
              slice.openEnd,
            );
          },
        },
      }),
    ];
  },
});

// variableNodesInFragment returns fragment with the registered variables
// of its text nodes as variable nodes, see textToVariableNodes.
function variableNodesInFragment(
  fragment: Fragment,
  schema: Schema,
  names: string[],
): Fragment {
  const nodes: PMNode[] = [];
  fragment.forEach((node) => {
    if (!node.isText || !node.text) {
      nodes.push(
        node.copy(variableNodesInFragment(node.content, schema, names)),
      );
      return;
    }
    let last = 0;
    for (const { from, to, name } of findEditorVariables(node.text, names)) {
      if (from > last) {
        nodes.push(schema.text(node.text.slice(last, from), node.marks));
      }
      nodes.push(schema.nodes[editorVariableNodeName].create({ name }));
      last = to;
    }
    if (last === 0) {
      nodes.push(node);
    } else if (last < node.text.length) {
      nodes.push(schema.text(node.text.slice(last), node.marks));
    }
  });
  return Fragment.from(nodes);
}

/**
 * textToVariableNodes returns doc with the registered variable `names`
 * of its text nodes as {@link EditorVariableNode}s. The text around a
 * variable keeps its marks.
 */
export function textToVariableNodes(
  doc: JSONContent,
  names: string[],
): JSONContent {
  if (doc.type === "text") {
    return doc;
  }
  if (!doc.content) {
    return doc;
  }

  const content: JSONContent[] = [];
  for (const child of doc.content) {
    if (child.type !== "text" || !child.text) {
      content.push(textToVariableNodes(child, names));
      continue;
    }
    let last = 0;
    for (const { from, to, name } of findEditorVariables(child.text, names)) {
      if (from > last) {
        content.push({ ...child, text: child.text.slice(last, from) });
      }
      content.push({ type: editorVariableNodeName, attrs: { name } });
      last = to;
    }
    if (last < child.text.length) {
      content.push(
        last === 0 ? child : { ...child, text: child.text.slice(last) },
      );
    }
  }

  return { ...doc, content };
}

/**
 * variableNodesToText returns doc with each {@link EditorVariableNode}
 * replaced by the variable's name as plain text, the form the renderers
 * of the text recognise.
 */
export function variableNodesToText(doc: JSONContent): JSONContent {
  if (doc.type === editorVariableNodeName) {
    return { type: "text", text: (doc.attrs?.name as string) ?? "" };
  }
  if (!doc.content) {
    return doc;
  }
  return { ...doc, content: doc.content.map(variableNodesToText) };
}
